import {
  type RoomNameMatcherOptions,
  distinctiveTokens,
  findMatchingRoomNames,
  isAllRoomsName,
  stripRoomNameNoise,
  tokenize,
} from '~/components/Offers/Detail/OfferBuilder/DealInformation/shared/roomNameMatcher';
import {
  getEgsMarginPercent,
  isArchivedRow,
} from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/egs/egsUtils';

import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { normalizeRoomsValue } from './egsWizardEngine';
import { EGS_BEDDING_LABELS, EGS_COLLECTED_BY_LABELS, EGS_GUEST_TYPE_LABELS } from './fields';
import type { EgsDraftInput, EgsGuestType } from './types';

export type PreviousEgs = {
  records: Array<EgsDraftInput>;
  roomNames: Array<string>;
  beddingToChoose: number;
  paidToHotel: number;
  otherCollectedBy: number;
  otherGuestType: number;
  otherCurrency: number;
};

export type EgsRoomRow = {
  key: string;
  name: string;
  guestTypes: Array<EgsGuestType>;
  suggestedRooms: Array<string>;
};

const AGE_RANGE = /^\s*(\d+)?\s*-\s*(\d+)?\s*$/;
const MIN_AGE = 0;
const MAX_AGE = 99;
const PAID_TO_HOTEL_LABEL = 'Paid directly to the Hotel';
const ALL_OF_KIND = /^\s*all\b(.*)$/i;
const MATCHER_OPTIONS: RoomNameMatcherOptions = { plurals: true };

function nameKey(name: string | undefined) {
  return (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function keyOfLabel<Key extends string>(labels: Record<Key, string>, label: string | null | undefined) {
  return (Object.keys(labels) as Array<Key>).find((key) => labels[key] === label?.trim());
}

function amount(value: number | string | null | undefined): number {
  return Number(value) || 0;
}

function ageRange(value: string | null | undefined) {
  const [, fromAge, toAge] = value?.match(AGE_RANGE) ?? [];
  return { fromAge: fromAge ? Number(fromAge) : MIN_AGE, toAge: toAge ? Number(toAge) : MAX_AGE };
}

function roomsOf(name: string, roomNames: Array<string>): 'all' | Array<string> {
  if (isAllRoomsName(name)) {
    return 'all';
  }
  const [, kind] = name.match(ALL_OF_KIND) ?? [];
  if (kind !== undefined) {
    const kindTokens = distinctiveTokens(stripRoomNameNoise(kind), MATCHER_OPTIONS);
    if (!kindTokens.length) {
      return 'all';
    }
    return roomNames.filter((roomName) => {
      const roomTokens = tokenize(roomName, MATCHER_OPTIONS);
      return kindTokens.every((token) => roomTokens.includes(token));
    });
  }
  return findMatchingRoomNames(stripRoomNameNoise(name), roomNames, MATCHER_OPTIONS);
}

export function reducePreviousEgs(
  surcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
  currency: string,
  roomNames: Array<string>,
): PreviousEgs {
  const result: PreviousEgs = {
    records: [],
    roomNames,
    beddingToChoose: 0,
    paidToHotel: 0,
    otherCollectedBy: 0,
    otherGuestType: 0,
    otherCurrency: 0,
  };

  for (const surcharge of surcharges) {
    if (isArchivedRow(surcharge)) {
      continue;
    }

    const guestType = keyOfLabel(EGS_GUEST_TYPE_LABELS, surcharge.extra_guest_type__c);
    const collectedBy = keyOfLabel(EGS_COLLECTED_BY_LABELS, surcharge.collected_by__c);
    const costPrice = amount(surcharge.cost_price__c);
    const sellPriceAud = amount(surcharge.sell_price_aud__c);

    if (!guestType) {
      result.otherGuestType += 1;
      continue;
    }
    if (!collectedBy) {
      if (surcharge.collected_by__c?.trim() === PAID_TO_HOTEL_LABEL) {
        result.paidToHotel += 1;
      } else {
        result.otherCollectedBy += 1;
      }
      continue;
    }
    if (surcharge.cost_price_currency !== currency && costPrice > 0) {
      result.otherCurrency += 1;
      continue;
    }

    const beddingType = keyOfLabel(EGS_BEDDING_LABELS, surcharge.bedding_type__c) ?? '';
    if (!beddingType) {
      result.beddingToChoose += 1;
    }
    const convertedCost = amount(surcharge.converted_cost__c);

    result.records.push({
      rooms: roomsOf(surcharge.name ?? '', roomNames),
      guestType,
      ...ageRange(surcharge.age_range__c),
      collectedBy,
      beddingType,
      otherBeddingType: surcharge.other_extra_bedding_type__c?.trim() ?? '',
      rateType: '',
      inclusions: surcharge.inclusions__c?.trim() ?? '',
      currency,
      costPrice,
      convertedCost,
      sellPriceAud,
      marginPct: getEgsMarginPercent(convertedCost, sellPriceAud),
      sourceName: surcharge.name?.trim(),
    });
  }

  return result;
}

export function egsRoomRows({ records, roomNames }: PreviousEgs): Array<EgsRoomRow> {
  const rows = new Map<string, EgsRoomRow>();
  for (const record of records) {
    const key = nameKey(record.sourceName);
    const row = rows.get(key) ?? {
      key,
      name: record.sourceName ?? '',
      guestTypes: [],
      suggestedRooms: record.rooms === 'all' ? roomNames : record.rooms,
    };
    if (!row.guestTypes.includes(record.guestType)) {
      row.guestTypes.push(record.guestType);
    }
    rows.set(key, row);
  }
  return [...rows.values()];
}

export function chosenEgsRooms(
  rows: Array<EgsRoomRow>,
  picked: Record<string, Array<string>>,
  roomNames: Array<string>,
): Record<string, Array<string>> {
  return Object.fromEntries(
    rows.map((row) => [row.key, (picked[row.key] ?? row.suggestedRooms).filter((room) => roomNames.includes(room))]),
  );
}

export function withChosenEgsRooms(
  { records, roomNames }: PreviousEgs,
  roomsByNameKey: Record<string, Array<string>>,
  skippedNameKeys: ReadonlySet<string>,
): Array<EgsDraftInput> {
  return records
    .filter((record) => !skippedNameKeys.has(nameKey(record.sourceName)))
    .map((record) => ({
      ...record,
      rooms: normalizeRoomsValue(roomsByNameKey[nameKey(record.sourceName)] ?? [], roomNames),
    }));
}
